import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef, useMemo, useState} from 'react';
import areEqual from 'react-fast-compare';

import {trackEventWithModule} from 'analytics/Analytics';
import {
  UPDATE_FLOWCHART_MODAL_TYPE,
  CREATE_PROCESS_MODAL_TYPE,
} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionOrigin,
  ActionMethod,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  CloseType,
} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {convertFlowchartGqlToProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/util/convertFlowchartGqlToProcessFlowchart';
import {convertGQLFlowchartConfigToClient} from 'processes/graph-builder-processes/flowchart-viz/util/convertGQLFlowchartConfigToClient';
import convertStaticProcessFlowchartForReactFlow from 'processes/graph-builder-processes/flowchart-viz/util/convertStaticProcessFlowchartForReactFlow';
import {flowchartHasChainedRouters} from 'processes/graph-builder-processes/flowchart-viz/util/flowchartHasChainedRouters';
import * as StubbableFunctions from 'processes/graph-builder-processes/flowchart-viz/util/stubbableFunctions';
import constructDesignSpace from 'processes/graph-builder-processes/util/constructDesignSpace';
import getEmptyFlowchart from 'processes/graph-builder-processes/util/getEmptyFlowchart';
import {ProcessFlowchartErrorType} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  ProcessFlowchartDesignSpace,
  ProcessTaskSchemaForGraphBuilderValidation,
  SchemaMapperConfig,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import ProcessFlowchartEditor from '../graph-builder-processes/flowchart-viz/ProcessFlowchartEditor';
import {ProcessInteractiveFlowchart} from '../graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  GetTaskSchemaWithFlowchartQuery,
  GetTaskSchemaWithFlowchartQueryVariables,
} from './__generated/GetTaskSchemaWithFlowchartQuery';
import {
  TaskSchemaWithFlowchartConfigQuery,
  TaskSchemaWithFlowchartConfigQueryVariables,
} from './__generated/TaskSchemaWithFlowchartConfigQuery';
import MinimalReadOnlyFlowchartViewer from './MinimalReadOnlyFlowchartViewer';

export const TASK_SCHEMA_WITH_FLOWCHART_CONFIG_QUERY = gql`
  query TaskSchemaWithFlowchartConfigQuery($processTaskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $processTaskSchemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
        flowchartConfig {
          id
          currentFlowchartConfigVersion {
            ...ProcessFlowchartConfigVersionGraphQL
            availableSchemas {
              ... on ProcessTaskSchemaNode {
                ...ProcessTaskSchemaGraphQL
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('ProcessFlowchartConfigVersion').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface CreateProcessGraphBuilderPaneBaseProps {
  flowchart: ProcessInteractiveFlowchart | null;
  flowchartSchema: {
    __typename: string;
    id: string;
    name: string;
    outputSchema: {id: string} | null;
    flowchartConfig: {
      currentFlowchartConfigVersion: {doesFlowchartConfigUseTaskDefinitions: boolean} | null;
    } | null;
  };
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void;
  onChangeEditStatus: (isEditing: boolean) => void;
  modalId: string;
  actionOrigin: string;
  modalType?: string;
  eventGroupId: string;
}
export interface CreateProcessGraphBuilderPaneProps extends CreateProcessGraphBuilderPaneBaseProps {
  flowchart: ProcessInteractiveFlowchart | null;
  designSpace: ProcessFlowchartDesignSpace;
  isFlowchartModifiableOnCreation: boolean;
}

function NoTemplateFlowchartProvided(): JSX.Element {
  return (
    <div className="u-padding u-text-align-center">
      No template flowchart was provided.
      <br />
      Create a new flowchart by pressing the Edit button above.
    </div>
  );
}

function FlowchartPaneWrapper(props: {
  children: JSX.Element;
  getButtons?: () => JSX.Element | null;
}): JSX.Element {
  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'CreateProcessGraphBuilderPane', element: 'root'})}
    >
      <div className={classNames('u-lr-split', css(styles.titleBar))}>
        <h5>Flowchart</h5>
        {props.getButtons != null ? <div>{props.getButtons()}</div> : null}
      </div>
      {props.children}
    </div>
  );
}

/**
 * This component is used inside the CreateProcessModal, allowing users to modify (or view) the flowchart
 * to be used for the task group.
 */
export function CreateProcessGraphBuilderPane(props: CreateProcessGraphBuilderPaneProps): JSX.Element {
  const {onChangeEditStatus} = props;
  const [isEditing, setIsEditing] = useState(false);
  const [flowchartErrors, setFlowchartErrors] = useState<Array<ProcessFlowchartErrorType>>([]);
  const [draftFlowchart, setDraftFlowchart] = useState(props.flowchart);
  const flowchartPaneKey = useRef(1);
  const locked = !props.isFlowchartModifiableOnCreation || !isEditing;
  const componentName = 'CreateProcessGraphBuilderPane';
  const [didInitialFlowchartHaveChainedRouters, setDidInitialFlowchartHaveChainedRouters] = useState(
    flowchartHasChainedRouters(draftFlowchart)
  );

  // Ensure that if the parent receives late data, we update our initial draft FC. Without this,
  // we can end up missing a valid template flowchart.
  useEffect(() => {
    if (!isEditing) {
      setDraftFlowchart(props.flowchart);
    }
    if (props.flowchart != null) {
      setDidInitialFlowchartHaveChainedRouters(flowchartHasChainedRouters(props.flowchart));
    }
    // We only want this to fire when the prop changes, not when `isEditing` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.flowchart]);

  useEffect(() => {
    onChangeEditStatus(isEditing);
    // We force the flowchart editor to rebuild itself each time the user changes edit status. This does
    // two things: (1) when cancelling, it forces a refresh from the original FC data, and (2) when the
    // user hits Done, it triggers it to re-run the "autofit" layout calculations.
    flowchartPaneKey.current++;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  function getButtons(): JSX.Element | null {
    if (!props.isFlowchartModifiableOnCreation) {
      return null;
    }
    return isEditing ? (
      <>
        <Button
          text="Cancel"
          onClick={() => {
            trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
              event_group_id: props.eventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_CREATION_MODAL_FLOWCHART_PANE,
              action_method: ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION,
              task_schema_api_id: props.flowchartSchema.id,
              close_type: CloseType.CANCEL,
              error_types: flowchartErrors,
              modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
            });
            setIsEditing(false);
            setDraftFlowchart(props.flowchart);
          }}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          className={css(styles.cancelButton)}
          size={ButtonSize.MINI}
          dataTestAttrs={{component: componentName, element: 'cancelButton'}}
        />
        <Button
          text="Done"
          disabled={flowchartErrors.length > 0}
          onClick={() => {
            trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
              event_group_id: props.eventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_CREATION_MODAL_FLOWCHART_PANE,
              action_method: ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION,
              task_schema_api_id: props.flowchartSchema.id,
              error_types: flowchartErrors,
              modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
            });
            setIsEditing(false);
            props.onChangeFlowchart(draftFlowchart!);
          }}
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MINI}
          dataTestAttrs={{component: componentName, element: 'doneButton'}}
        />
      </>
    ) : (
      <Button
        text="Edit"
        icon="edit"
        onClick={() => {
          trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
            event_group_id: props.eventGroupId,
            action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_CREATION_MODAL_FLOWCHART_PANE,
            action_method: ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION,
            task_schema_api_id: props.flowchartSchema.id,
            modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
          });
          setIsEditing(true);
          if (draftFlowchart == null) {
            setDraftFlowchart(getEmptyFlowchart(props.flowchartSchema));
          }
        }}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        dataTestAttrs={{component: componentName, element: 'editButton'}}
      />
    );
  }

  return (
    <FlowchartPaneWrapper getButtons={getButtons}>
      {draftFlowchart == null ? (
        <NoTemplateFlowchartProvided />
      ) : (
        <div className={css(styles.flowchartEditorWrapper)}>
          <ProcessFlowchartEditor
            key={flowchartPaneKey.current}
            flowchart={draftFlowchart}
            designSpace={props.designSpace}
            locked={locked}
            hideSidebar={locked}
            onFlowchartChange={(newFlowchart, flowchartErrors) => {
              setDraftFlowchart(newFlowchart);
              setFlowchartErrors(flowchartErrors);
            }}
            editorEventGroupId={props.modalId}
            actionMethod={ActionMethod.AT_CREATION_FLOWCHART_MODIFICATION}
            didInitialFlowchartHaveChainedRouters={didInitialFlowchartHaveChainedRouters}
            modalType={CREATE_PROCESS_MODAL_TYPE}
          />
        </div>
      )}
    </FlowchartPaneWrapper>
  );
}

enum UseDesignSpaceSentinel {
  LOADING = 'LOADING',
  INACCESSIBLE = 'INACCESSIBLE',
  NO_FLOWCHART_CONFIG = 'NO_FLOWCHART_CONFIG',
}

interface UseDesignSpaceResult {
  designSpace: ProcessFlowchartDesignSpace;
  initialFlowchart: ProcessInteractiveFlowchart | null;
  isFlowchartModifiableOnCreation: boolean;
  schemaMapperConfigs: Array<SchemaMapperConfig>;
}

function useDesignSpace(
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation
): UseDesignSpaceResult | UseDesignSpaceSentinel {
  const taskSchemaWithFlowchartConfigResult = useBenchlingQuery<
    TaskSchemaWithFlowchartConfigQuery,
    TaskSchemaWithFlowchartConfigQueryVariables
  >(TASK_SCHEMA_WITH_FLOWCHART_CONFIG_QUERY, {
    variables: {processTaskSchemaId: flowchartSchema.id},
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  if (taskSchemaWithFlowchartConfigResult.type !== QueryResultType.SUCCESS) {
    return UseDesignSpaceSentinel.LOADING;
  }

  const taskSchema = taskSchemaWithFlowchartConfigResult.data.processTaskSchema;
  if (isInaccessible(taskSchema)) {
    return UseDesignSpaceSentinel.INACCESSIBLE;
  }
  if (taskSchema.flowchartConfig?.currentFlowchartConfigVersion == null) {
    return UseDesignSpaceSentinel.NO_FLOWCHART_CONFIG;
  }

  const flowchartConfigVersion = taskSchema.flowchartConfig.currentFlowchartConfigVersion;
  const availableTaskSchemas = accessibleObjs<ProcessTaskSchemaGraphQL>(
    flowchartConfigVersion.availableSchemas
  );
  const clientConfig = convertGQLFlowchartConfigToClient(
    flowchartConfigVersion,
    availableTaskSchemas,
    taskSchema
  );
  return {
    initialFlowchart: clientConfig.templateFlowchart,
    designSpace: constructDesignSpace(
      availableTaskSchemas,
      clientConfig.schemaMapperConfigs,
      flowchartSchema
    ),
    schemaMapperConfigs: compact(
      clientConfig.schemaMapperConfigs.map((config) =>
        config.fromTaskSchemas.length > 0 && config.toTaskSchema != null
          ? {
              clientId: config.clientId,
              fromTaskSchemaIds: config.fromTaskSchemas.map(({id}) => id),
              toTaskSchemaId: config.toTaskSchema.id,
            }
          : null
      )
    ),
    isFlowchartModifiableOnCreation: flowchartConfigVersion.isFlowchartModifiableOnCreation,
  };
}

export interface QueryingCreateProcessGraphBuilderPaneProps extends CreateProcessGraphBuilderPaneBaseProps {
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation & {
    flowchartConfig: {
      currentFlowchartConfigVersion: {doesFlowchartConfigUseTaskDefinitions: boolean} | null;
    } | null;
  };
  onInitializeFlowchart: (
    flowchart: ProcessInteractiveFlowchart | null,
    schemaMapperConfigs: Array<SchemaMapperConfig>
  ) => void;
}

function isDesignSpaceResult(
  result: UseDesignSpaceResult | UseDesignSpaceSentinel
): result is UseDesignSpaceResult {
  return (result as UseDesignSpaceResult).designSpace !== undefined;
}

export function CreateProcessGraphBuilderPaneWithoutTaskDefinitions(
  props: QueryingCreateProcessGraphBuilderPaneProps
): JSX.Element {
  const designSpaceResult = useDesignSpace(props.flowchartSchema);
  const previousDesignSpaceResultRef = useRef<UseDesignSpaceResult | UseDesignSpaceSentinel | null>(null);
  useEffect(() => {
    if (
      isDesignSpaceResult(designSpaceResult) &&
      !areEqual(previousDesignSpaceResultRef.current, designSpaceResult) &&
      props.flowchart == null
    ) {
      props.onInitializeFlowchart(designSpaceResult.initialFlowchart, designSpaceResult.schemaMapperConfigs);
    }
    previousDesignSpaceResultRef.current = designSpaceResult;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designSpaceResult]);

  if (designSpaceResult === UseDesignSpaceSentinel.LOADING) {
    return (
      <FlowchartPaneWrapper>
        <CenteredHorizontalThrobber />
      </FlowchartPaneWrapper>
    );
  } else if (designSpaceResult === UseDesignSpaceSentinel.INACCESSIBLE) {
    return (
      <FlowchartPaneWrapper>
        <>No access</>
      </FlowchartPaneWrapper>
    );
  } else if (designSpaceResult === UseDesignSpaceSentinel.NO_FLOWCHART_CONFIG) {
    return (
      <FlowchartPaneWrapper>
        <>No flowchart has been configured for this schema</>
      </FlowchartPaneWrapper>
    );
  }

  return (
    <CreateProcessGraphBuilderPane
      {...props}
      flowchart={props.flowchart}
      designSpace={designSpaceResult.designSpace}
      isFlowchartModifiableOnCreation={designSpaceResult.isFlowchartModifiableOnCreation}
    />
  );
}

export const GET_TASK_SCHEMA_WITH_FLOWCHART_WITH_TASK_DEFINITIONS_QUERY = gql`
  query GetTaskSchemaWithFlowchartQuery($processTaskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $processTaskSchemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
        flowchartConfig {
          id
          currentFlowchartConfigVersion {
            id
            templateFlowchart {
              ...ProcessFlowchartForEditorGraphQL
            }
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('ProcessFlowchartForEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function CreateProcessGraphBuilderPaneWithTaskDefinitions(
  props: QueryingCreateProcessGraphBuilderPaneProps
): JSX.Element {
  const result = useBenchlingQuery<GetTaskSchemaWithFlowchartQuery, GetTaskSchemaWithFlowchartQueryVariables>(
    GET_TASK_SCHEMA_WITH_FLOWCHART_WITH_TASK_DEFINITIONS_QUERY,
    {
      variables: {processTaskSchemaId: props.flowchartSchema.id},
    }
  );

  const templateFlowchart =
    result.type === QueryResultType.SUCCESS &&
    !isInaccessible(result.data.processTaskSchema) &&
    result.data.processTaskSchema.flowchartConfig?.currentFlowchartConfigVersion?.templateFlowchart
      ? result.data.processTaskSchema.flowchartConfig.currentFlowchartConfigVersion.templateFlowchart
      : null;
  const interactiveFlowchart = useMemo(
    () => (templateFlowchart ? convertFlowchartGqlToProcessInteractiveFlowchart(templateFlowchart) : null),
    [templateFlowchart]
  );

  useEffect(() => {
    if (interactiveFlowchart != null) {
      props.onInitializeFlowchart(interactiveFlowchart, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveFlowchart]);

  const {ref, width: reactFlowContainerWidth} = StubbableFunctions.useResizeObserver();

  if (result.type !== QueryResultType.SUCCESS) {
    return (
      <FlowchartPaneWrapper>
        <CenteredHorizontalThrobber />
      </FlowchartPaneWrapper>
    );
  }
  if (isInaccessible(result.data.processTaskSchema)) {
    return (
      <FlowchartPaneWrapper>
        <>No access</>
      </FlowchartPaneWrapper>
    );
  } else if (interactiveFlowchart == null) {
    return (
      <FlowchartPaneWrapper>
        <>No flowchart has been configured for this schema</>
      </FlowchartPaneWrapper>
    );
  }

  const flowchartRenderData = convertStaticProcessFlowchartForReactFlow(interactiveFlowchart);

  return (
    <FlowchartPaneWrapper>
      <div ref={ref} className={css(styles.wrapper)}>
        <MinimalReadOnlyFlowchartViewer
          renderData={flowchartRenderData}
          reactFlowContainerWidth={reactFlowContainerWidth ?? 0}
          flowchartClientId={interactiveFlowchart.clientId}
        />
      </div>
    </FlowchartPaneWrapper>
  );
}

export default function QueryingCreateProcessGraphBuilderPane(
  props: QueryingCreateProcessGraphBuilderPaneProps
): JSX.Element {
  if (
    props.flowchartSchema.flowchartConfig?.currentFlowchartConfigVersion
      ?.doesFlowchartConfigUseTaskDefinitions
  ) {
    return <CreateProcessGraphBuilderPaneWithTaskDefinitions {...props} />;
  }
  return <CreateProcessGraphBuilderPaneWithoutTaskDefinitions {...props} />;
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
  },
  titleBar: {
    background: WHITE,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '10px 20px',
    height: '40px',
  },
  flowchartEditorWrapper: {
    height: 'calc(100% - 40px)',
  },
  cancelButton: {
    marginRight: '10px',
  },
});
